import { Box } from '@mui/material';
import type { ReactElement } from 'react';

import type { ColorTokens } from '../tokens';

/**
 * Pastille d'initiales d'une personne (maquette « BO/Avatar », quatre tons).
 *
 * Elle sert a la barre superieure (l'operateur connecte), au journal d'audit et
 * aux fiches (l'auteur d'une action) : partout ou un nom est accompagne d'un
 * repere visuel. **Elle ne remplace jamais le nom** — elle est decorative
 * (`aria-hidden`), le nom ecrit a cote est ce qui identifie.
 *
 * Aucune photo : le back-office n'affiche pas l'avatar qu'un parent a choisi
 * dans l'application, qui est une donnee personnelle sans utilite ici.
 */
export type AvatarTone = 'pink' | 'mint' | 'blue' | 'yellow';

const AVATAR_TONES: Readonly<Record<AvatarTone, { readonly ink: keyof ColorTokens; readonly fill: keyof ColorTokens }>> = {
  pink: { ink: 'accentPinkStrong', fill: 'accentPinkSoft' },
  mint: { ink: 'accentMintStrong', fill: 'accentMintSoft' },
  blue: { ink: 'accentBlueStrong', fill: 'accentBlueSoft' },
  yellow: { ink: 'accentYellowStrong', fill: 'accentYellowSoft' },
};

/**
 * Deux initiales au plus : premiere lettre du premier et du dernier mot, ou des
 * deux premieres lettres d'un mot seul. Une adresse ne garde que sa partie
 * locale (« lea.dupont@… » -> « LD »). Vide : « ? », jamais une pastille vide.
 */
export const initialsOf = (name: string): string => {
  const local = name.includes('@') ? (name.split('@')[0] ?? '') : name;
  const words = local.split(/[\s._-]+/u).filter((word) => word !== '');

  if (words.length === 0) {
    return '?';
  }

  const letters =
    words.length === 1
      ? Array.from(words[0]!).slice(0, 2)
      : [Array.from(words[0]!)[0], Array.from(words[words.length - 1]!)[0]];

  return letters.join('').toLocaleUpperCase('fr-FR');
};

export interface AvatarProps {
  /** Nom ou adresse de la personne ; seules ses initiales sont affichees. */
  readonly name: string;
  readonly tone?: AvatarTone;
  readonly size?: number;
}

export const Avatar = ({ name, tone = 'pink', size = 32 }: AvatarProps): ReactElement => {
  const { ink, fill } = AVATAR_TONES[tone];

  return (
    <Box
      component="span"
      aria-hidden
      data-tone={tone}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        bgcolor: `tokens.${fill}`,
        color: `tokens.${ink}`,
        fontSize: Math.round(size * 0.375),
        fontWeight: 800,
      }}
    >
      {initialsOf(name)}
    </Box>
  );
};
